<template>
	<BaseModal
		:title="t('title')"
		v-model="show"
		@transitionShow="setTransitionShow"
		@close="onClose()"
		noPadding
	>
		<div class="flex w-full gap-3 px-5">
			<PriceInput
				class="flex-1"
				v-model="localMin"
				@enter="enter"
				title="min"
				:placeholder="minValue ? minValue.toString() : '0'"
				@focusout="checkMin"
			/>
			<PriceInput
				class="flex-1"
				v-model="localMax"
				ref="input"
				title="max"
				:placeholder="maxValue ? maxValue.toString() : '∞'"
				@focusout="checkMax"
			/>
		</div>
		<BottomActionsModal class="mt-5 pt-4">
			<BaseButton size="lg" color="primary" class="w-full" @click="close()">
				{{ t('confirm') }}
			</BaseButton>
		</BottomActionsModal>
	</BaseModal>
</template>
<script setup lang="ts">
import BaseModal from './BaseModal.vue'
import { ref, watch, nextTick } from 'vue'
import PriceInput from '../PriceInput.vue'
import BaseButton from '../BaseButton.vue'
import { useI18n } from 'vue-i18n'
import BottomActionsModal from '../BottomActionsModal.vue'
import { sleep } from '@/utils/sleep'

const { t } = useI18n({
	messages: {
		en: {
			title: 'price',
			confirm: 'confirm'
		},
		ru: {
			title: 'цена',
			confirm: 'подтвердить'
		}
	}
})

const props = defineProps<{
	minValue?: number
	maxValue?: number
}>()

let transitionShow = false

const setTransitionShow = async (value: boolean) => {
	transitionShow = value

	await nextTick()

	if (show.value) {
		focusInput()
	}
	if (!show.value) {
		focusOut()
	}
}

const show = defineModel<boolean>('show')
const min = defineModel<number>('min')
const max = defineModel<number>('max')

const localMin = ref<number>()
const localMax = ref<number>()

const input = ref<InstanceType<typeof PriceInput>>()

const focusInput = async () => {
	if (!transitionShow) {
		return
	}

	await nextTick()
	await sleep(10)

	if (!input.value) {
		return
	}

	input.value.focusInput()
}

const focusOut = async () => {
	if (transitionShow) {
		return
	}

	await nextTick()

	if (!input.value) {
		return
	}

	input.value.focusOut()
}

watch(show, () => {
	if (!show.value) {
		focusOut()
		return
	}

	localMin.value = min.value
	localMax.value = max.value

	focusInput()
})

const onClose = () => {
	min.value = localMin.value
	max.value = localMax.value
}

const close = () => {
	onClose()

	show.value = false
}

const enter = () => {
	show.value = false

	focusInput()
}

const checkMin = () => {
	if (!min.value) {
		min.value = undefined
		return
	}
	if (min.value < 0) {
		min.value = undefined
		return
	}
	if (isNaN(min.value)) {
		min.value = undefined
		return
	}

	if (max.value && min.value > max.value) {
		min.value = max.value
		return
	}
	if (props.minValue && min.value < props.minValue) {
		min.value = props.minValue
		return
	}
}

const checkMax = () => {
	if (!max.value) {
		max.value = undefined
		return
	}
	if (max.value < 0) {
		max.value = undefined
		return
	}
	if (isNaN(max.value)) {
		max.value = undefined
		return
	}

	if (min.value && max.value < min.value) {
		max.value = min.value
		return
	}
	if (props.maxValue && max.value > props.maxValue) {
		max.value = props.maxValue
		return
	}
}
</script>
